import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, ShieldCheck, Truck, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getProduct, listCategories } from '../lib/api';
import { iconFor } from '../lib/icons';

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, cats] = await Promise.all([getProduct(slug), listCategories()]);
        if (cancelled) return;
        setProduct(p);
        setCategory(cats.find((c) => c.key === p.category_key) || null);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <>
        <div className="telegra-bg" aria-hidden="true" />
        <Header />
        <main className="pt-[100px] sm:pt-[120px] px-6 pb-20 max-w-3xl mx-auto text-center">
          <h1 data-testid="product-not-found" className="font-display uppercase text-4xl text-[color:var(--alfa-navy)]">Product not found</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-neutral-950 hover:text-neutral-950">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  if (!product || !category) {
    return (
      <>
        <div className="telegra-bg" aria-hidden="true" />
        <Header />
        <main className="pt-[130px] sm:pt-[150px] px-6 pb-32 max-w-3xl mx-auto text-center">
          <p className="uppercase text-[12.5px] tracking-[0.28em] text-neutral-950">Loading…</p>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = iconFor(product.icon_key);
  const quizPath = `/${category.slug}/quiz`;

  return (
    <>
      <div className="telegra-bg" aria-hidden="true" />
      <Header />
      <main className="pt-[72px] sm:pt-[88px]">
        {/* Breadcrumb */}
        <div className="px-4 sm:px-6 pt-6">
          <div className="max-w-[1360px] mx-auto text-[13.5px] uppercase tracking-[0.14em] text-neutral-950">
            <Link to="/" className="hover:text-neutral-900">Home</Link>
            <span className="mx-2">/</span>
            <Link to={`/${category.slug}`} className="hover:text-neutral-900">{category.name}</Link>
            <span className="mx-2">/</span>
            <span className="text-neutral-900">{product.name}</span>
          </div>
        </div>

        {/* Product hero */}
        <section className="px-4 sm:px-6 py-10 sm:py-14">
          <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-start">
            <div className="relative aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-orange-50 via-white to-purple-50 border border-neutral-900/10">
              {product.img ? (
                <img src={product.img} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-40 h-40 rounded-full bg-white border border-neutral-900/10 flex items-center justify-center shadow-lg">
                    <Icon size={72} strokeWidth={1.2} className="text-neutral-950" />
                  </div>
                </div>
              )}
              <span className="absolute top-5 left-5 bg-white text-neutral-900 text-[12.5px] font-semibold uppercase tracking-widest rounded-sm px-3 py-1.5 shadow-sm">{product.tag}</span>
            </div>

            <div>
              <p className="uppercase text-[12.5px] tracking-[0.24em] text-neutral-950">{category.name}</p>
              <h1 data-testid="product-detail-name" className="mt-3 font-display uppercase text-[40px] sm:text-[56px] md:text-[72px] leading-[0.95] tracking-[-0.015em] text-[color:var(--alfa-navy)]">{product.name}</h1>
              <p className="mt-4 text-[16px] sm:text-[19px] text-neutral-950 italic">{product.subtitle}</p>
              <div className="mt-6 flex items-baseline gap-3">
                <span data-testid="product-detail-price" className="font-display text-[52px] leading-none text-neutral-950">{product.price}</span>
                <span className="text-[14.5px] uppercase tracking-wider text-neutral-950">{product.unit}</span>
              </div>
              <p className="mt-5 text-[17px] text-neutral-950 leading-relaxed">{product.desc}</p>
              <ul className="mt-6 space-y-2">
                {product.chips.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-[16px] text-neutral-950">
                    <Check size={16} className="text-emerald-600 mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={quizPath} className="btn-primary arrow-suffix inline-flex items-center uppercase text-[14.5px] tracking-[0.14em] font-semibold px-6 py-4 rounded-sm">
                  Get Started
                </Link>
                <Link to={`/${category.slug}`} className="inline-flex items-center gap-2 bg-white/80 hover:bg-white text-neutral-900 uppercase text-[14.5px] tracking-[0.14em] font-semibold px-6 py-4 rounded-sm border border-neutral-300">
                  View all {category.name}
                </Link>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[14.5px] text-neutral-950">
                <div className="flex items-center gap-2 rounded-md border border-neutral-900/10 bg-white/60 p-3">
                  <ShieldCheck size={16} className="text-emerald-600" /> Physician-reviewed
                </div>
                <div className="flex items-center gap-2 rounded-md border border-neutral-900/10 bg-white/60 p-3">
                  <Truck size={16} className="text-neutral-900" /> 2–3 day shipping
                </div>
                <div className="flex items-center gap-2 rounded-md border border-neutral-900/10 bg-white/60 p-3">
                  <Star size={16} className="text-amber-500" fill="currentColor" strokeWidth={0} /> 4.9/5 satisfaction
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important safety information */}
        <section className="px-4 sm:px-6 py-12">
          <div className="max-w-[1360px] mx-auto rounded-md border border-neutral-900/10 bg-white/60 backdrop-blur p-6 sm:p-8">
            <h3 className="font-display uppercase text-[18px] text-[color:var(--alfa-navy)]">Important Safety Information</h3>
            <p className="mt-3 text-[15px] text-neutral-950 leading-relaxed">
              Prescription medications are provided only after review and approval by a licensed U.S. healthcare provider. Individual results vary. Not appropriate for everyone. Talk to your provider about your medical history and current medications before starting any new treatment. Compounded medications are prepared based on specific patient needs and have not been reviewed by the FDA for safety or effectiveness.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
